import { Button, Checkbox, Input, Textarea } from '@artosera/ui';
import { useId, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DELAIS_DEMANDE_SITE,
  MOYENS_CONTACT_SITE,
  raisonErreur,
  useEnvoyerDemandeSite,
  type CorpsDemandeSite,
  type DelaiDemandeSite,
  type DemandeSiteVue,
  type LangueDemandeSite,
  type MoyenContactSite,
} from '../../api/demandeSite.js';
import { estApiError } from '../../api/errors.js';
import { Field } from '../Field.js';
import { useErreurFormulaire } from '../useErreurFormulaire.js';
import styles from './DemandeSitePage.module.css';

interface Choix<T extends string> {
  readonly valeur: T;
  readonly libelle: string;
}

interface GroupeChoixProps<T extends string> {
  readonly legende: string;
  readonly nom: string;
  readonly valeur: T;
  readonly choix: readonly Choix<T>[];
  readonly onChange: (valeur: T) => void;
  readonly erreur?: string | undefined;
  /** Place nécessaire aux tuiles : `court` (deux mots), `deux` ou `trois` colonnes. */
  readonly colonnes?: 'court' | 'deux' | 'trois';
}

/**
 * Un choix unique en tuiles : des radios natifs (clavier, lecteur d'écran)
 * habillés en cibles de 44 px, plus lisibles qu'un menu à quatre entrées.
 */
function GroupeChoix<T extends string>({
  legende,
  nom,
  valeur,
  choix,
  onChange,
  erreur,
  colonnes = 'court',
}: GroupeChoixProps<T>): JSX.Element {
  const idErreur = useId();
  return (
    <fieldset className={styles.groupe} aria-describedby={erreur ? idErreur : undefined}>
      <legend className={styles.legende}>{legende}</legend>
      <div className={styles.choixZone}>
        <div className={styles.choix} data-colonnes={colonnes}>
          {choix.map((option) => (
            <label key={option.valeur} className={styles.tuile}>
              <input
                type="radio"
                name={nom}
                value={option.valeur}
                checked={valeur === option.valeur}
                onChange={() => onChange(option.valeur)}
              />
              <span className={styles.puce} aria-hidden="true" />
              <span>{option.libelle}</span>
            </label>
          ))}
        </div>
      </div>
      {erreur ? (
        <p id={idErreur} className={styles.erreurChamp} role="alert">
          {erreur}
        </p>
      ) : null}
    </fieldset>
  );
}

export interface DemandeSiteFormulaireProps {
  readonly galerieSlug: string;
  readonly nomInitial: string;
  readonly emailInitial: string;
  readonly onEnvoyee: (demande: DemandeSiteVue) => void;
  readonly onDejaEnCours: () => void;
}

const CHEMINS_AFFICHES = [
  'siteExistant',
  'urlActuelle',
  'objectifs',
  'langues',
  'delai',
  'budget',
  'message',
  'contact',
] as const;

/**
 * Le formulaire de demande de création de site (direction seule). Les erreurs
 * de champ viennent d'abord des règles que l'écran sait vérifier, puis de
 * l'API (`details.champs`) ; un `409` rend la main à la page, qui montre la
 * demande déjà ouverte.
 */
export function DemandeSiteFormulaire({
  galerieSlug,
  nomInitial,
  emailInitial,
  onEnvoyee,
  onDejaEnCours,
}: DemandeSiteFormulaireProps): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const envoi = useEnvoyerDemandeSite(galerieSlug);
  const erreurApi = useErreurFormulaire();

  const [siteExistant, setSiteExistant] = useState(false);
  const [urlActuelle, setUrlActuelle] = useState('');
  const [objectifs, setObjectifs] = useState('');
  const [langues, setLangues] = useState<readonly LangueDemandeSite[]>(['fr', 'en']);
  const [visiteVirtuelle, setVisiteVirtuelle] = useState(false);
  const [delai, setDelai] = useState<DelaiDemandeSite>('des_que_possible');
  const [budget, setBudget] = useState('');
  const [message, setMessage] = useState('');
  const [nom, setNom] = useState(nomInitial);
  const [email, setEmail] = useState(emailInitial);
  const [telephone, setTelephone] = useState('');
  const [moyen, setMoyen] = useState<MoyenContactSite>('email');
  const [avisEnvoi, setAvisEnvoi] = useState<string | null>(null);
  const [locales, setLocales] = useState<Readonly<Record<string, string>>>({});

  const erreurDe = (...chemins: readonly string[]): string | undefined =>
    chemins.map((chemin) => locales[chemin]).find(Boolean) ?? erreurApi.messageChamp(...chemins);

  function basculerLangue(langue: LangueDemandeSite, active: boolean): void {
    setLangues((courantes) =>
      active
        ? courantes.includes(langue)
          ? courantes
          : [...courantes, langue]
        : courantes.filter((autre) => autre !== langue),
    );
  }

  function verifier(): Record<string, string> {
    const manques: Record<string, string> = {};
    const obligatoire = t('regie:validation.obligatoire');
    const adresse = urlActuelle.trim();
    if (siteExistant && adresse === '')
      manques.urlActuelle = t('regie:demandeSite.erreurs.urlObligatoire');
    else if (siteExistant && !/^https?:\/\/\S+$/i.test(adresse))
      manques.urlActuelle = t('regie:demandeSite.erreurs.urlInvalide');
    if (objectifs.trim() === '') manques.objectifs = obligatoire;
    if (langues.length === 0) manques.langues = t('regie:demandeSite.erreurs.langueObligatoire');
    if (nom.trim() === '') manques['contact.nom'] = obligatoire;
    if (email.trim() === '') manques['contact.email'] = obligatoire;
    if (moyen === 'telephone' && telephone.trim() === '')
      manques['contact.telephone'] = t('regie:demandeSite.erreurs.telephoneObligatoire');
    return manques;
  }

  function envoyer(evenement: FormEvent<HTMLFormElement>): void {
    evenement.preventDefault();
    erreurApi.reinitialiser();
    setAvisEnvoi(null);
    const manques = verifier();
    setLocales(manques);
    if (Object.keys(manques).length > 0) return;

    const texteMessage = message.trim();
    const corps: CorpsDemandeSite = {
      siteExistant,
      urlActuelle: siteExistant ? urlActuelle.trim() : null,
      objectifs: objectifs.trim(),
      langues,
      visiteVirtuelle,
      delai,
      budget: budget.trim() === '' ? null : budget.trim(),
      ...(texteMessage === '' ? {} : { message: texteMessage }),
      contact: {
        nom: nom.trim(),
        email: email.trim(),
        telephone: telephone.trim() === '' ? null : telephone.trim(),
        moyen,
      },
    };

    envoi.mutateAsync(corps).then(
      ({ demande }) => onEnvoyee(demande),
      (cause: unknown) => {
        if (estApiError(cause) && cause.status === 409) {
          onDejaEnCours();
          return;
        }
        if (raisonErreur(cause) === 'quota_demande_site') {
          setAvisEnvoi(t('regie:demandeSite.erreurs.quota'));
          return;
        }
        erreurApi.appliquer(cause, CHEMINS_AFFICHES);
      },
    );
  }

  const idUrl = useId();
  const idObjectifs = useId();
  const idBudget = useId();
  const idMessage = useId();
  const idNom = useId();
  const idEmail = useId();
  const idTelephone = useId();
  const idVisite = useId();
  const idFr = useId();
  const idEn = useId();
  const erreurLangues = erreurDe('langues');

  const choixDelai = DELAIS_DEMANDE_SITE.map((valeur) => ({
    valeur,
    libelle: t(`regie:demandeSite.formulaire.delai.${valeur}`),
  }));
  const choixMoyen = MOYENS_CONTACT_SITE.map((valeur) => ({
    valeur,
    libelle: t(`regie:demandeSite.formulaire.contact.moyen.${valeur}`),
  }));

  return (
    <form className={styles.formulaire} onSubmit={envoyer} noValidate>
      <header className={styles.formulaireEntete}>
        <h2 className={styles.titrePanneau}>{t('regie:demandeSite.formulaire.titre')}</h2>
        <p className={styles.aide}>{t('regie:demandeSite.formulaire.aide')}</p>
      </header>

      <div className={styles.section}>
        <GroupeChoix
          legende={t('regie:demandeSite.formulaire.siteExistant.legende')}
          nom="siteExistant"
          valeur={siteExistant ? 'oui' : 'non'}
          choix={[
            { valeur: 'oui', libelle: t('regie:demandeSite.formulaire.siteExistant.oui') },
            { valeur: 'non', libelle: t('regie:demandeSite.formulaire.siteExistant.non') },
          ]}
          onChange={(valeur) => setSiteExistant(valeur === 'oui')}
        />
        {siteExistant ? (
          <Field
            label={t('regie:demandeSite.formulaire.urlActuelle.label')}
            htmlFor={idUrl}
            error={erreurDe('urlActuelle')}
          >
            <Input
              id={idUrl}
              type="url"
              inputMode="url"
              autoComplete="url"
              placeholder="https://"
              maxLength={500}
              value={urlActuelle}
              onChange={(evenement) => setUrlActuelle(evenement.target.value)}
            />
            <p className={styles.aide}>{t('regie:demandeSite.formulaire.urlActuelle.aide')}</p>
          </Field>
        ) : null}
      </div>

      <div className={styles.section}>
        <Field
          label={t('regie:demandeSite.formulaire.objectifs.label')}
          htmlFor={idObjectifs}
          error={erreurDe('objectifs')}
        >
          <Textarea
            id={idObjectifs}
            rows={4}
            maxLength={2000}
            required
            value={objectifs}
            onChange={(evenement) => setObjectifs(evenement.target.value)}
          />
          <p className={styles.aide}>{t('regie:demandeSite.formulaire.objectifs.aide')}</p>
        </Field>

        <fieldset
          className={styles.groupe}
          aria-describedby={erreurLangues ? `${idFr}-erreur` : undefined}
        >
          <legend className={styles.legende}>
            {t('regie:demandeSite.formulaire.langues.legende')}
          </legend>
          <div className={styles.choixZone}>
            <div className={styles.choix} data-colonnes="court">
              <Checkbox
                id={idFr}
                label={t('regie:demandeSite.formulaire.langues.fr')}
                checked={langues.includes('fr')}
                onCheckedChange={(actif) => basculerLangue('fr', actif === true)}
              />
              <Checkbox
                id={idEn}
                label={t('regie:demandeSite.formulaire.langues.en')}
                checked={langues.includes('en')}
                onCheckedChange={(actif) => basculerLangue('en', actif === true)}
              />
            </div>
          </div>
          {erreurLangues ? (
            <p id={`${idFr}-erreur`} className={styles.erreurChamp} role="alert">
              {erreurLangues}
            </p>
          ) : null}
        </fieldset>

        <Checkbox
          id={idVisite}
          label={t('regie:demandeSite.formulaire.visite')}
          checked={visiteVirtuelle}
          onCheckedChange={(actif) => setVisiteVirtuelle(actif === true)}
        />

        <GroupeChoix
          legende={t('regie:demandeSite.formulaire.delai.legende')}
          nom="delai"
          valeur={delai}
          choix={choixDelai}
          onChange={setDelai}
          erreur={erreurDe('delai')}
          colonnes="deux"
        />

        <Field
          label={t('regie:demandeSite.formulaire.budget.label')}
          htmlFor={idBudget}
          error={erreurDe('budget')}
        >
          <Input
            id={idBudget}
            maxLength={200}
            value={budget}
            onChange={(evenement) => setBudget(evenement.target.value)}
          />
          <p className={styles.aide}>{t('regie:demandeSite.formulaire.budget.aide')}</p>
        </Field>

        <Field
          label={t('regie:demandeSite.formulaire.message.label')}
          htmlFor={idMessage}
          error={erreurDe('message')}
        >
          <Textarea
            id={idMessage}
            rows={3}
            maxLength={5000}
            value={message}
            onChange={(evenement) => setMessage(evenement.target.value)}
          />
        </Field>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sousTitre}>{t('regie:demandeSite.formulaire.contact.titre')}</h3>
        <div className={styles.grille2}>
          <Field
            label={t('regie:demandeSite.formulaire.contact.nom')}
            htmlFor={idNom}
            error={erreurDe('contact.nom')}
          >
            <Input
              id={idNom}
              autoComplete="name"
              maxLength={120}
              required
              value={nom}
              onChange={(evenement) => setNom(evenement.target.value)}
            />
          </Field>
          <Field
            label={t('regie:demandeSite.formulaire.contact.email')}
            htmlFor={idEmail}
            error={erreurDe('contact.email')}
          >
            <Input
              id={idEmail}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(evenement) => setEmail(evenement.target.value)}
            />
          </Field>
        </div>
        <GroupeChoix
          legende={t('regie:demandeSite.formulaire.contact.moyen.legende')}
          nom="moyen"
          valeur={moyen}
          choix={choixMoyen}
          onChange={setMoyen}
          colonnes="trois"
        />
        <Field
          label={t(
            moyen === 'telephone'
              ? 'regie:demandeSite.formulaire.contact.telephone'
              : 'regie:demandeSite.formulaire.contact.telephoneFacultatif',
          )}
          htmlFor={idTelephone}
          error={erreurDe('contact.telephone')}
        >
          <Input
            id={idTelephone}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            maxLength={40}
            required={moyen === 'telephone'}
            value={telephone}
            onChange={(evenement) => setTelephone(evenement.target.value)}
          />
        </Field>
      </div>

      {(avisEnvoi ?? erreurApi.erreur) ? (
        <p className={styles.erreur} role="alert">
          {avisEnvoi ?? erreurApi.erreur}
        </p>
      ) : null}

      <div className={styles.actions}>
        <Button type="submit" loading={envoi.isPending}>
          {t('regie:demandeSite.formulaire.envoyer')}
        </Button>
      </div>
    </form>
  );
}
